/**
 * Emailing an account invoice to the account, with the PDF attached.
 * Sent through the signed-in Microsoft account (Graph /me/sendMail).
 */
import { getOrganizationEmail } from '../../constants/organizations';
import { ensureSignedIn, getGraphClient, getMsalInstance, GRAPH_SIGNIN_REQUIRED_TO_SEND } from '../../utils/graphService';
import { displayDate, type AccountInvoiceView } from './accountInvoice.model';
import type { DirectoryAccount } from './accounts.model';
import { formatGbp, money } from './arrangements.model';

export type InvoiceEmailDraft = {
  to: string;
  cc: string;
  subject: string;
  body: string;
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Splits "a@x.com; b@y.com" into addresses. */
export function parseEmailList(value: string): string[] {
  return value.split(/[;,\s]+/).map((part) => part.trim()).filter(Boolean);
}

/** Returns the first problem with the draft, or null when it can be sent. */
export function validateInvoiceEmail(draft: InvoiceEmailDraft): string | null {
  const to = parseEmailList(draft.to);
  if (to.length === 0) return 'Enter who to send it to.';
  const bad = [...to, ...parseEmailList(draft.cc)].find((address) => !EMAIL_PATTERN.test(address));
  if (bad) return `${bad} does not look like an email address.`;
  if (!draft.subject.trim()) return 'Enter a subject.';
  if (!draft.body.trim()) return 'Write a message.';
  return null;
}

/** Pre-filled email for an invoice: who it is to, the amount payable and when, and how to pay. */
export function defaultInvoiceEmail(args: {
  invoice: AccountInvoiceView;
  account: DirectoryAccount | null;
  companyName: string;
  bank?: { name?: string | null; accountName?: string | null; sortCode?: string | null; accountNumber?: string | null };
  senderName?: string | null;
}): InvoiceEmailDraft {
  const { invoice, account, companyName, bank } = args;
  const greetingName = account?.contactName?.trim().split(/\s+/)[0] || account?.companyName || 'there';
  const payable = invoice.cisDeduction != null && invoice.cisDeduction > 0
    ? money(invoice.grossTotal - invoice.cisDeduction)
    : invoice.grossTotal;
  const what = invoice.title ? ` for ${invoice.title}` : '';
  const due = invoice.paymentTermsInDays === 0
    ? 'due on receipt'
    : `due by ${displayDate(invoice.dueDate)}`;
  const cisLine = invoice.cisDeduction != null && invoice.cisDeduction > 0
    ? ` (after the CIS deduction of ${formatGbp(invoice.cisDeduction)})`
    : '';
  const bankLines = [
    bank?.accountName && `Account name: ${bank.accountName}`,
    bank?.sortCode && `Sort code: ${bank.sortCode}`,
    bank?.accountNumber && `Account number: ${bank.accountNumber}`,
    bank?.name && `Bank: ${bank.name}`,
  ].filter(Boolean);
  const signOff = ['Kind regards,', args.senderName, companyName].filter(Boolean).join('\n');
  const to = account?.billingEmail || account?.email || '';

  // A paid invoice is sent for their records: confirm payment, don't ask for it
  if (invoice.status === 'paid') {
    return {
      to,
      cc: '',
      subject: `Paid invoice ${invoice.invoiceRef} from ${companyName}`,
      body: [
        `Dear ${greetingName},`,
        `Please find attached invoice ${invoice.invoiceRef}${what}, for your records. It has been paid in full (${formatGbp(payable)}${cisLine}) — thank you.`,
        'If you have any questions, just reply to this email.',
        signOff,
      ].join('\n\n'),
    };
  }

  const paragraphs = [
    `Dear ${greetingName},`,
    `Please find attached invoice ${invoice.invoiceRef}${what}. The amount payable is ${formatGbp(payable)}${cisLine}, ${due}.`,
    bankLines.length ? `Payment details:\n${bankLines.join('\n')}\nPlease use ${invoice.invoiceRef} as the payment reference.` : '',
    'If you have any questions, just reply to this email.',
    signOff,
  ].filter(Boolean);
  return {
    to,
    cc: '',
    subject: `Invoice ${invoice.invoiceRef} from ${companyName}`,
    body: paragraphs.join('\n\n'),
  };
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Plain text with blank-line paragraphs, as HTML for the email body. */
export function invoiceEmailHtml(body: string): string {
  return body
    .trim()
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph).replace(/\n/g, '<br />')}</p>`)
    .join('');
}

/** Account invoices are sent from the Keystone South UK mailbox. */
export const ACCOUNT_INVOICE_SENDER = getOrganizationEmail('Keystone South UK');

/** True when the signed-in Microsoft account is not the sending mailbox (needs Send As permission). */
export function sendsOnBehalf(signedIn: string | null | undefined): boolean {
  const mailbox = String(signedIn || '').trim().toLowerCase();
  return !!mailbox && mailbox !== ACCOUNT_INVOICE_SENDER.address.toLowerCase();
}

/** The Microsoft account invoices will be sent from, if signed in. */
export function signedInMailbox(): string | null {
  try {
    const instance = getMsalInstance();
    const account = instance.getActiveAccount() || instance.getAllAccounts()[0];
    return account?.username || null;
  } catch {
    return null;
  }
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error || new Error('Could not read the PDF'));
    reader.readAsDataURL(blob);
  });
}

/** Sends the email with the invoice PDF attached. Nothing is sent until Microsoft sign-in succeeds. */
export async function sendInvoiceEmail(args: {
  draft: InvoiceEmailDraft;
  pdf: Blob;
  fileName: string;
}): Promise<void> {
  const problem = validateInvoiceEmail(args.draft);
  if (problem) throw new Error(problem);
  if (!(await ensureSignedIn())) throw new Error(GRAPH_SIGNIN_REQUIRED_TO_SEND);
  const client = await getGraphClient();
  const recipients = (value: string) => parseEmailList(value).map((address) => ({ emailAddress: { address } }));
  await client.api('/me/sendMail').post({
    message: {
      subject: args.draft.subject.trim(),
      body: { contentType: 'HTML', content: invoiceEmailHtml(args.draft.body) },
      from: { emailAddress: { address: ACCOUNT_INVOICE_SENDER.address, name: ACCOUNT_INVOICE_SENDER.name } },
      sender: { emailAddress: { address: ACCOUNT_INVOICE_SENDER.address, name: ACCOUNT_INVOICE_SENDER.name } },
      toRecipients: recipients(args.draft.to),
      ccRecipients: recipients(args.draft.cc),
      attachments: [{
        '@odata.type': '#microsoft.graph.fileAttachment',
        name: args.fileName,
        contentType: 'application/pdf',
        contentBytes: await blobToBase64(args.pdf),
      }],
    },
    saveToSentItems: true,
  });
}
